import React from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiTrash2,
} from "react-icons/fi";

import type { SurveyQuestion, SurveyQuestionType } from "../../lib/api";
import OptionsEditor from "./OptionsEditor";
import SuggestionsEditor from "./SuggestionsEditor";
import { QUESTION_TYPES, isChoiceType, isScaleType, isTextType } from "./surveyTypes";

type Props = {
  question: SurveyQuestion;
  index: number;
  total: number;
  active: boolean;
  /** True once the survey is live — structure locks, wording stays editable. */
  locked?: boolean;
  invalid?: boolean;
  onFocus: () => void;
  onChange: (patch: Partial<SurveyQuestion>) => void;
  onRetype: (type: SurveyQuestionType) => void;
  onMove: (direction: -1 | 1) => void;
  onDuplicate: () => void;
  onRemove: () => void;
};

const iconButton =
  "rounded-lg border border-[#2C363E] p-2 text-[#5C6B76] transition hover:border-[#3A4852] hover:text-[#C6D0D7] disabled:cursor-not-allowed disabled:opacity-30";

const QuestionEditor: React.FC<Props> = ({
  question,
  index,
  total,
  active,
  locked,
  invalid,
  onFocus,
  onChange,
  onRetype,
  onMove,
  onDuplicate,
  onRemove,
}) => (
  <section
    onFocusCapture={onFocus}
    onClick={onFocus}
    className={`rounded-2xl border bg-[#161E25]/40 p-4 transition ${
      invalid
        ? "border-red-500/45"
        : active
        ? "border-secondary_button/60"
        : "border-[#2C363E]"
    }`}
  >
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#5C6B76]">
          Question {index + 1}
        </span>
        <span className="rounded-md border border-[#3A4852] bg-[#1C2227] px-1.5 py-0.5 font-mono text-[10px] text-[#5C6B76]">
          {question.key}
        </span>
      </div>

      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={locked || index === 0}
          aria-label="Move question up"
          className={iconButton}
        >
          <FiChevronUp className="text-sm" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={locked || index === total - 1}
          aria-label="Move question down"
          className={iconButton}
        >
          <FiChevronDown className="text-sm" />
        </button>
        <button
          type="button"
          onClick={onDuplicate}
          disabled={locked}
          aria-label="Duplicate question"
          className={iconButton}
        >
          <FiCopy className="text-sm" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={locked}
          aria-label="Delete question"
          className="rounded-lg border border-[#2C363E] p-2 text-[#5C6B76] transition hover:border-red-500/40 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <FiTrash2 className="text-sm" />
        </button>
      </div>
    </div>

    <div className="space-y-3">
      <input
        type="text"
        value={question.prompt}
        onChange={(e) => onChange({ prompt: e.target.value })}
        placeholder="What do you want to ask?"
        className="h-11 w-full rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[14px] font-medium text-white outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button"
      />

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={String(question.type)}
          disabled={locked}
          onChange={(e) => onRetype(e.target.value as SurveyQuestionType)}
          className="h-10 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition focus:border-secondary_button disabled:opacity-60"
        >
          {QUESTION_TYPES.map((t) => (
            <option key={String(t.type)} value={String(t.type)}>
              {t.label}
            </option>
          ))}
        </select>

        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[#C6D0D7]">
          <input
            type="checkbox"
            checked={question.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            className="h-3.5 w-3.5 accent-secondary_button"
          />
          Required
        </label>
      </div>

      {isChoiceType(question.type) && (
        <OptionsEditor question={question} disabled={locked} onChange={onChange} />
      )}

      {isTextType(question.type) && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <input
              type="text"
              value={question.placeholder ?? ""}
              onChange={(e) => onChange({ placeholder: e.target.value })}
              placeholder="Placeholder text"
              className="h-10 min-w-0 flex-1 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button"
            />
            <label className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[12px] text-[#869AA9]">
              Max
              <input
                type="number"
                min={20}
                max={2000}
                value={question.maxLength ?? 600}
                onChange={(e) =>
                  onChange({ maxLength: Number(e.target.value) || null })
                }
                className="w-16 bg-transparent text-[13px] tabular-nums text-[#E7EBEE] outline-none"
              />
            </label>
          </div>
          <SuggestionsEditor
            suggestions={question.suggestions ?? []}
            disabled={locked}
            onChange={(next) => onChange({ suggestions: next })}
          />
        </div>
      )}

      {isScaleType(question.type) && (
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            value={question.scale?.minLabel ?? ""}
            onChange={(e) =>
              onChange({
                scale: {
                  min: question.scale?.min ?? 0,
                  max: question.scale?.max ?? 10,
                  minLabel: e.target.value,
                  maxLabel: question.scale?.maxLabel ?? null,
                },
              })
            }
            placeholder="Label for the low end"
            className="h-10 min-w-0 flex-1 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button"
          />
          <input
            type="text"
            value={question.scale?.maxLabel ?? ""}
            onChange={(e) =>
              onChange({
                scale: {
                  min: question.scale?.min ?? 0,
                  max: question.scale?.max ?? 10,
                  minLabel: question.scale?.minLabel ?? null,
                  maxLabel: e.target.value,
                },
              })
            }
            placeholder="Label for the high end"
            className="h-10 min-w-0 flex-1 rounded-lg border border-[#2C363E] bg-[#13181B] px-3 text-[13px] text-[#E7EBEE] outline-none transition placeholder:text-[#5C6B76] focus:border-secondary_button"
          />
        </div>
      )}
    </div>
  </section>
);

export default QuestionEditor;
